<template>
  <div>
    <h2 style="padding-bottom: 20px">Declaration Summary</h2>

    <h4>Your Declaration ID is {{ currentDeclaration.declarationNumber }}</h4>

    <barcode v-bind:value="currentDeclaration.declarationNumber" format="CODE128">
      {{ currentDeclaration.declarationNumber }}
    </barcode>

    <p>
      Please print this page or note down the Declaration ID and provide it to
      your MaltaPost post office.
    </p>
    <p>
      The Declaration ID and the declared information is valid for
      {{ expiresInDays }} days. After {{ expiresInDays }} days the data will be
      deleted.
    </p>

    The following information has been recorded:
    <v-container>
      <v-row>
        <v-col cols="12" md="6">
          <div>Sender</div>
          <div class="text-sm-body-2 text-body-2">
            <v-container>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Name: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.name }}
                    {{ currentDeclaration.sender.surname }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Address: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>{{ currentDeclaration.sender.addressLine1 }}</span>
                  <span v-if="currentDeclaration.sender.addressLine2"
                    ><br />{{ currentDeclaration.sender.addressLine2 }}</span
                  >
                  <span><br />{{ currentDeclaration.sender.postcode }}</span>
                  <span><br />{{ currentDeclaration.sender.locality }}</span>
                  <span
                    ><br />{{
                      getCountryNameById(currentDeclaration.sender.countryId)
                    }}</span
                  >
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.sender.email">
                <v-col cols="4" md="3">
                  <span> Email: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.email }}
                  </span>
                </v-col>
              </v-row>

              <v-row no-gutters v-if="currentDeclaration.sender.phone">
                <v-col cols="4" md="3">
                  <span> Phone: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.phone }}
                  </span>
                </v-col>
              </v-row>

              <v-row no-gutters v-if="currentDeclaration.sender.vatNumber">
                <v-col cols="4" md="3">
                  <span> Vat No: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.vatNumber }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.sender.voecNumber && getCountryRequiresVoec(currentDeclaration.recipient.countryId)">
                <v-col cols="4" md="3">
                  <span> VOEC No: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.sender.voecNumber }}
                  </span>
                </v-col>
              </v-row>

            </v-container>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div>Recipient</div>
          <div class="text-sm-body-2 text-body-2">
            <v-container>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Name: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.name }}
                    {{ currentDeclaration.recipient.surname }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="4" md="3">
                  <span> Address: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>{{ currentDeclaration.recipient.addressLine1 }}</span>
                  <span v-if="currentDeclaration.recipient.addressLine2"
                    ><br />{{ currentDeclaration.recipient.addressLine2 }}</span
                  >
                  <span><br />{{ currentDeclaration.recipient.postcode }}</span>
                  <span><br />{{ currentDeclaration.recipient.locality }}</span>
                  <span
                    ><br />{{
                      getCountryNameById(currentDeclaration.recipient.countryId)
                    }}</span
                  >
                </v-col>
              </v-row>

              <v-row no-gutters v-if="currentDeclaration.recipient.email">
                <v-col cols="4" md="3">
                  <span> Email: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.email }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.recipient.phone">
                <v-col cols="4" md="3">
                  <span> Phone: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.phone }}
                  </span>
                </v-col>
              </v-row>
              <v-row no-gutters v-if="currentDeclaration.recipient.vatNumber">
                <v-col cols="4" md="3">
                  <span> Vat No: </span>
                </v-col>
                <v-col cols="8" md="8">
                  <span>
                    {{ currentDeclaration.recipient.vatNumber }}
                  </span>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-col>
      </v-row>

      <div>Category of Parcel Content</div>
      <div class="text-sm-body-2 text-body-2">
        <v-container style="margin-bottom: 10px">
          {{ currentDeclaration.contentCategory }}
          <span v-if="requiresContentCategoryDescription"
            >({{ currentDeclaration.contentCategoryDescription }})</span
          >
        </v-container>
      </div>

      <div v-if="currentDeclaration.recipient.isRequiresContentItems">
        Content Items
      </div>
      <div
        v-if="currentDeclaration.recipient.isRequiresContentItems"
        class="text-sm-body-2 text-body-2"
      >
        <v-expansion-panels class="d-md-none" :hover="true">
          <v-expansion-panel
            v-for="item in currentDeclaration.contentItems"
            :key="item.key"
          >
            <v-expansion-panel-header>{{
              item.description
            }}</v-expansion-panel-header>
            <v-expansion-panel-content>
              <p>
                <span class="font-weight-bold">DESCRIPTION: </span
                ><span>{{ item.description }}</span>
              </p>
              <p>
                <span class="font-weight-bold">HS CODE: </span
                ><span>{{ item.hsCode }}</span>
              </p>
              <p>
                <span class="font-weight-bold">COUNTRY OF ORIGIN: </span
                ><span>{{ getCountryNameById(item.countryOfOriginId) }}</span>
              </p>
              <p>
                <span class="font-weight-bold">TOTAL QUANTITY: </span
                ><span>{{ item.quantity }}</span>
              </p>
              <p>
                <span class="font-weight-bold">VALUE (€): </span
                ><span>{{ item.value }}</span>
              </p>
              <p>
                <span class="font-weight-bold">WEIGHT PER ITEM (grams): </span
                ><span>{{ item.weight }}</span>
              </p>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-simple-table class="d-none d-md-block">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left">Decription</th>
                <th class="text-left">HS Code</th>
                <th class="text-left">Country of Origin</th>
                <th class="text-left">Total Quantity</th>
                <th class="text-left">Value (&euro;)</th>
                <th class="text-left">Weight per Item (g)</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in currentDeclaration.contentItems"
                :key="item.key"
              >
                <td>{{ item.description }}</td>
                <td>{{ item.hsCode }}</td>
                <td>{{ getCountryNameById(item.countryOfOriginId) }}</td>
                <td>{{ item.quantity }}</td>
                <td>{{ item.value }}</td>
                <td>{{ item.weight }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-container>

    <v-container>
      <v-row no-gutters>
        <v-col cols="12" sm="4" class="pa-2">
          <v-btn
            color="primary"
            block
            class="mb-2"
            @click="printLabel()"
            :disabled="printingLabel"
          >
            Print Label
            <v-progress-circular
              indeterminate
              :size="20"
              color="primary"
              v-if="printingLabel"
              class="ml-2"
            ></v-progress-circular>
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4" class="pa-2">
          <v-btn
            color="primary"
            block
            class="mb-2"
            @click="declareAnotherItem()"
          >
            Send Another Item
          </v-btn>
        </v-col>
        <v-col cols="12" sm="4" class="pa-2">
          <v-btn color="primary" block class="mb-2" @click="goHome()">
            Home
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import router from "../router";
import VueBarcode from 'vue-barcode';
const { mapGetters } = createNamespacedHelpers("declarations");

export default {
  name: "Summary",
  components: {
    'barcode': VueBarcode
  },
  data: () => ({}),
  computed: {
    ...mapGetters([
      "currentDeclaration",
      "countries",
      "requiresContentCategoryDescription",
    ]),
    expiresInDays() {
      return this.$store.getters.getDeclarationExpiryInDays;
    },
    printingLabel: {
      set(printing) {
        this.$store.dispatch("declarations/setPrintingLabel", printing);
      },
      get() {
        return this.$store.getters["declarations/printingLabel"];
      },
    },
  },
  methods: {
    getCountryNameById: function (id) {
      const country = this.countries.find(function (country) {
        return country.id === id;
      });
      return country ? country.name : "";
    },
    getCountryRequiresVoec: function (id) {
      if (id) {
        const country = this.countries.find(function (country) {
          return country.id === id;
        });
        return country ? country.requireVOEC: false;
      }
      return false;
    },    
    printLabel: function () {
      this.$store.dispatch("declarations/printLabel");
    },
    declareAnotherItem: function () {
      this.$store.dispatch(
        "declarations/startAnotherDeclarationForCurrentSender"
      );
    },
    goHome: function () {
      router.push({ name: "Intro" });
    },
  },
  mounted() {
    if (!this.currentDeclaration.declarationNumber) {
      //redirect to homepage
      router.push("/");
    }
  },
};
</script>

<style scoped>
.md-progress-bar {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
}
</style>
